import { initializeApp, getApps, getApp } from 'firebase/app';
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  sendPasswordResetEmail, 
  signOut, 
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  addDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { getAnalytics, isSupported } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: "AIzaSyAjoM-rNKo6OsR6gOAXWh2BtcHJnLEQ66A",
  authDomain: "nobishiro-auth.firebaseapp.com",
  projectId: "nobishiro-auth",
  storageBucket: "nobishiro-auth.firebasestorage.app",
  messagingSenderId: "755052166963",
  appId: "1:755052166963:web:bc42e4476f240728d4c846",
  measurementId: "G-LZETKTWGPV"
};

const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();

export const db = getFirestore(app);
export const auth = getAuth(app);

// 認証状態をlocalStorageに永続化（Safari・スマホ対応）
setPersistence(auth, browserLocalPersistence).catch(err => {
  console.warn('setPersistence error:', err);
});

let analytics = null;
const initAnalytics = async () => {
  try {
    const supported = await isSupported();
    if (supported) {
      const { initializeAnalytics } = await import("firebase/analytics");
      analytics = initializeAnalytics(app);
    }
  } catch (err) {}
};
initAnalytics();
export const getAnalyticsInstance = () => analytics;

export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

export const signInWithEmail = async (email, password) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return { success: true, user: result.user };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const signUpWithEmail = async (email, password, displayName) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await setDoc(doc(db, 'users', result.user.uid), {
      uid: result.user.uid,
      email,
      displayName,
      role: 'user',
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
      provider: 'email'
    });
    return { success: true, user: result.user };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Googleでログイン（全デバイスpopup統一 - SafariのITP対策）
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    await _saveGoogleUserToFirestore(result.user);
    return { success: true, user: result.user };
  } catch (error) {
    // popupがブロックされた場合はredirectにフォールバック
    if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user') {
      try {
        await signInWithRedirect(auth, googleProvider);
        return { success: true, redirecting: true };
      } catch (redirectError) {
        return { success: false, error: redirectError.message };
      }
    }
    return { success: false, error: error.message };
  }
};

// ✅ リダイレクト後の結果を処理（Login.jsx の useEffect から呼ぶ）
export const handleRedirectResult = async () => {
  try {
    const result = await getRedirectResult(auth);
    if (!result) {
      return { success: false, noResult: true };
    }
    await _saveGoogleUserToFirestore(result.user);
    return { success: true, user: result.user };
  } catch (error) {
    console.error('handleRedirectResult error:', error);
    return { success: false, error: error.message };
  }
};

// Firestoreへのユーザー保存（内部ヘルパー）
const _saveGoogleUserToFirestore = async (user) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) {
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        role: 'user',
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        provider: 'google'
      });
    } else {
      await setDoc(doc(db, 'users', user.uid), {
        lastLogin: serverTimestamp()
      }, { merge: true });
    }
  } catch (err) {
    console.error('Firestore user save error:', err);
  }
};

export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const signOutUser = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getUserProfile = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      return { success: true, data: userDoc.data() };
    }
    return { success: false, error: 'ユーザーが見つかりません' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const logUserActivity = async (uid, activity) => {
  try {
    await addDoc(collection(db, 'user_activities'), {
      uid,
      activity,
      timestamp: serverTimestamp(),
      userAgent: navigator.userAgent
    });
  } catch (error) {
    console.error('Activity logging failed:', error);
  }
};

export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

export { app };
export default app;
